<script lang="tsx" setup>
import type { TabsPaneContext } from 'element-plus';

import type { VbenFormProps } from '#/adapter/form';
import type { VxeGridProps } from '#/adapter/vxe-table';
import type { OrderInfo } from '#/types';

import { computed, h, ref } from 'vue';

import { useVbenDrawer, useVbenModal, VbenButton } from '@vben/common-ui';
import { useUserStore } from '@vben/stores';

import {
  ElDescriptions,
  ElDescriptionsItem,
  ElDropdown,
  ElDropdownItem,
  ElDropdownMenu,
  ElForm,
  ElFormItem,
  ElLink,
  ElMessage,
  ElMessageBox,
  ElOption,
  ElSelect,
  ElTabPane,
  ElTabs,
} from 'element-plus';

import { useVbenVxeGrid } from '#/adapter/vxe-table';
import {
  buyerAccountOperateApi,
  cancelOrderApi,
  exportOrderItemListApi,
  getBuyerList,
  getChannelListApi,
  orderItemListApi,
  setOrderSysNoteApi,
  stagingBuyConfirmApi,
  stagingBuyRejectApi,
} from '#/api';
import { applyRefundApi, preOnlineApi } from '#/api/core';
import downloadPage from '#/components/file/downloadPage.vue';
import InputTag from '#/components/form/inputTag.vue';
import AddOrder from '#/components/order/add.vue';
import FieldTemplateGrid from '#/components/order/FieldTemplateGrid/index.vue';
import OrderInfoVue from '#/components/order/info.vue';
import BatchSetOrderLabelModel from '#/components/order/Label/BatchSetOrderLabelModel.vue';
import SelectOrderLabel from '#/components/order/Label/SelectOrderLabel.vue';
import UploadShippingModelTem from '#/components/order/UploadShippingModel.vue';
import ExpressLabelModalTem from '#/components/transferHouse/ExpressLabelModel.vue';
import LogisticsInfoModelTem from '#/components/transferHouse/LogisticsInfoModel.vue';
import { $t } from '#/locales';
import { useDicEnumStore } from '#/store';
import BuyBOrder from '#/views/market/internationalLogistics/BuyBOrder.vue';
import LogisticsTracking from '#/views/market/internationalLogistics/LogisticsTracking.vue';

const dicEnumStore = useDicEnumStore();
const userStore = useUserStore();
const {
  order_type_list,
  logistics_status,
  proxy_mode_list,
  have_shipping_label_list,
  staging_order_status,
} = dicEnumStore.dicEnum;
const canSelfPurchase = computed(
  () => userStore.userInfo?.showMerchantPlugin === true,
);

const defaultTime = [
  new Date(2000, 1, 1, 0, 0, 0),
  new Date(2000, 2, 1, 23, 59, 59),
];

interface FormValues {
  createDate: string;
  extraOrderTime: string;
  extraExpireTime: string;
  productName: string;
  productSku: string;
  extraShopName: string;
  extraOrderCode: string;
  orderStatus?: number | string | undefined;
  beginExtraOrderTime?: string;
  endExtraOrderTime?: string;
  beginExtraExpireTime?: string;
  endExtraExpireTime?: string;
  beginCreateDate?: string;
  endCreateDate?: string;
  pageSize?: number;
  pageNum?: number;
  platformArrivalDateUs?: string;
  platformArrivalDate?: string;
  beginPlatformArrivalDateUs?: string;
  endPlatformArrivalDateUs?: string;
  beginPlatformArrivalDate?: string;
  endPlatformArrivalDate?: string;
  orderByType?: number;
}

const orderByTypeList = ref([
  {
    label: '订单提交时间降序',
    value: 0,
    icon: 'icon-[mdi--sort-ascending]',
  },
  {
    label: '订单提交时间升序',
    value: 1,
    icon: 'icon-[mdi--sort-descending]',
  },
  {
    label: '顾客付款时间降序',
    value: 2,
    icon: 'icon-[mdi--sort-ascending]',
  },
  {
    label: '顾客付款时间升序',
    value: 3,
    icon: 'icon-[mdi--sort-descending]',
  },
]);

const orderByType = ref(0);

const formOptions: VbenFormProps = {
  // 默认展开
  collapsed: false,
  commonConfig: {
    labelWidth: 160,
  },
  schema: [
    {
      component: 'DatePicker',
      fieldName: 'createDate',
      label: $t('page.order.createDate'),

      componentProps: {
        startPlaceholder: $t('page.order.startTime'),
        endPlaceholder: $t('page.order.endTime'),
        type: 'datetimerange',
        'value-format': 'YYYY/MM/DD HH:mm:ss',
        format: 'YYYY/MM/DD HH:mm:ss',
        'time-format': 'HH:mm:ss',
        'default-time': defaultTime,
      },
    },
    {
      component: 'DatePicker',
      fieldName: 'extraOrderTime',
      label: $t('page.order.extraOrderTime'),
      componentProps: {
        startPlaceholder: $t('page.order.startTime'),
        endPlaceholder: $t('page.order.endTime'),
        type: 'datetimerange',
        'value-format': 'YYYY/MM/DD HH:mm:ss',
        format: 'YYYY/MM/DD HH:mm:ss',
        'time-format': 'HH:mm:ss',
        'default-time': defaultTime,
      },
    },
    {
      component: 'DatePicker',
      fieldName: 'extraExpireTime',
      label: $t('page.order.extraExpireTime'),
      componentProps: {
        startPlaceholder: $t('page.order.startTime'),
        endPlaceholder: $t('page.order.endTime'),
        type: 'datetimerange',
        'value-format': 'YYYY/MM/DD HH:mm:ss',
        format: 'YYYY/MM/DD HH:mm:ss',
        'time-format': 'HH:mm:ss',
        'default-time': defaultTime,
      },
    },
    {
      component: 'DatePicker',
      fieldName: 'platformArrivalDate',
      label: $t('page.order.platformArrivalDate'),
      componentProps: {
        startPlaceholder: $t('page.order.startTime'),
        endPlaceholder: $t('page.order.endTime'),
        type: 'datetimerange',
        'value-format': 'YYYY/MM/DD HH:mm:ss',
        format: 'YYYY/MM/DD HH:mm:ss',
        'time-format': 'HH:mm:ss',
        'default-time': defaultTime,
      },
    },
    {
      component: 'DatePicker',
      fieldName: 'platformArrivalDateUs',
      label: $t('page.order.platformArrivalDateUs'),
      componentProps: {
        startPlaceholder: $t('page.order.startTime'),
        endPlaceholder: $t('page.order.endTime'),
        type: 'datetimerange',
        'value-format': 'YYYY/MM/DD HH:mm:ss',
        format: 'YYYY/MM/DD HH:mm:ss',
        'time-format': 'HH:mm:ss',
        'default-time': defaultTime,
      },
    },

    {
      component: 'Input',
      componentProps: {
        placeholder: $t('page.order.productName'),
      },
      fieldName: 'productName',
      label: $t('page.order.productName'),
    },
    {
      component: h(InputTag, {
        placeholder: '双击批量搜索',
        clearable: true,
        collapseTags: true,
        collapseTagsTooltip: true,
      }),
      fieldName: 'productSkuList',
      label: $t('page.order.productSku'),
    },
    {
      component: h(InputTag, {
        placeholder: '双击批量搜索',
        clearable: true,
        collapseTags: true,
        collapseTagsTooltip: true,
      }),
      fieldName: 'extraOrderCodeList',
      label: $t('page.order.extraOrderCode'),
    },
    {
      component: h(InputTag, {
        placeholder: '双击批量搜索',
        clearable: true,
        collapseTags: true,
        collapseTagsTooltip: true,
      }),
      fieldName: 'platformDeliveryNoList',
      label: $t('page.order.platformDeliveryNo'),
    },
    {
      component: h(InputTag, {
        placeholder: '双击批量搜索',
        clearable: true,
        collapseTags: true,
        collapseTagsTooltip: true,
      }),
      fieldName: 'orderNoList',
      label: $t('page.order.orderNo'),
    },
    {
      component: 'Input',
      componentProps: {
        placeholder: $t('page.order.customerNote'),
      },
      fieldName: 'customerNote',
      label: $t('page.order.customerNote'),
    },
    {
      component: 'Select',
      fieldName: 'labelIds',
      componentProps: {
        multiple: true,
      },
      label: '订单标签',
    },
    // {
    //   component: h(InputTag, {
    //     placeholder: '双击批量搜索',
    //     clearable: true,
    //     collapseTags: true,
    //     collapseTagsTooltip: true,
    //   }),
    //   fieldName: 'waybillSnList',
    //   componentProps: {
    //     multiple: true,
    //   },
    //   label: 'B单号',
    // },
    // {
    //   component: 'Select',
    //   fieldName: 'haveWaybill',
    //   componentProps: {
    //     options: [
    //       {
    //         label: '有',
    //         value: true,
    //       },
    //       {
    //         label: '无',
    //         value: false,
    //       },
    //     ],
    //     clearable: true,
    //   },
    //   label: '是否有B单号',
    // },
    {
      component: 'Input',
      componentProps: {
        placeholder: $t('page.order.extraShopName'),
      },
      fieldName: 'extraShopName',
      label: $t('page.order.extraShopName'),
    },
    {
      component: 'Select',
      fieldName: 'purchaseChannelId',
      componentProps: {
        options: [],
        clearable: true,
      },
      label: '采购渠道',
    },
    {
      component: 'Select',
      fieldName: 'splitStatus',
      componentProps: {
        options: [
          {
            label: '是',
            value: 1,
          },
          {
            label: '否',
            value: 0,
          },
        ],
        clearable: true,
      },
      label: '是否拆单',
    },
    {
      component: h(InputTag, {
        placeholder: '双击批量搜索',
        clearable: true,
        collapseTags: true,
        collapseTagsTooltip: true,
      }),
      fieldName: 'stateList',
      label: $t('page.order.state'),
    },
    {
      component: 'Select',
      fieldName: 'orderType',
      componentProps: {
        options: order_type_list,
        clearable: true,
      },
      label: '下单类型',
    },
    {
      component: 'Select',
      fieldName: 'logisticsStatus',
      componentProps: {
        options: logistics_status.map((item) => ({
          label: item.name,
          value: item.type,
        })),
        clearable: true,
      },
      label: '物流状态',
    },
    {
      component: 'Select',
      fieldName: 'buyerAccountId',
      componentProps: {
        options: [],
        clearable: true,
      },
      label: '买家号',
    },
    {
      component: 'Select',
      fieldName: 'orderMode',
      componentProps: {
        options: proxy_mode_list.map((item) => ({
          label: item.label,
          value: item.value,
        })),
        clearable: true,
      },
      label: '订单类型',
    },
    {
      component: h(InputTag, {
        placeholder: '双击批量搜索',
        clearable: true,
        collapseTags: true,
        collapseTagsTooltip: true,
      }),
      fieldName: 'stateList',
      componentProps: {
        multiple: true,
      },
      label: '州',
    },
    {
      component: 'Select',
      fieldName: 'haveShippingLabel',
      label: '是否有官方面单',
      componentProps: {
        options: (have_shipping_label_list || []).map(
          (item: Record<string, any>) => {
            return {
              label: item.label,
              value: item.value,
            };
          },
        ),
      },
    },
    {
      component: 'Select',
      fieldName: 'stagingOrderStatus',
      componentProps: {
        options: staging_order_status.map((item) => ({
          label: item.name,
          value: item.type,
        })),
        clearable: true,
      },
      label: '仓库状态',
    },
  ],
  // 控制表单是否显示折叠按钮
  showCollapseButton: true,
  submitButtonOptions: {
    content: '查询',
  },
  // 是否在字段值改变时提交表单
  submitOnChange: false,
  // 按下回车时是否提交表单
  submitOnEnter: true,
};

const tableTotal = ref(0);
const formParams = ref();
const requestParams = (params: FormValues) => {
  const data: FormValues = Object.assign({}, params);
  data.orderByType = orderByType.value;
  // data.labelIds = labelIds.value;
  if (orderStatus.value !== 'all') {
    data.orderStatus = orderStatus.value;
  }
  if (Array.isArray(data.extraOrderTime)) {
    data.beginExtraOrderTime = data.extraOrderTime[0];
    data.endExtraOrderTime = data.extraOrderTime[1];
  }
  if (Array.isArray(data.extraExpireTime)) {
    data.beginExtraExpireTime = data.extraExpireTime[0];
    data.endExtraExpireTime = data.extraExpireTime[1];
  }
  if (Array.isArray(data.createDate)) {
    data.beginCreateDate = data.createDate[0];
    data.endCreateDate = data.createDate[1];
  }
  if (Array.isArray(data.platformArrivalDateUs)) {
    data.beginPlatformArrivalDateUs = data.platformArrivalDateUs[0];
    data.endPlatformArrivalDateUs = data.platformArrivalDateUs[1];
  }
  if (Array.isArray(data.platformArrivalDate)) {
    data.beginPlatformArrivalDate = data.platformArrivalDate[0];
    data.endPlatformArrivalDate = data.platformArrivalDate[1];
  }
  return data;
};

const gridOptions: VxeGridProps<OrderInfo> = {
  checkboxConfig: {
    highlight: true,
    reserve: true,
  },
  rowConfig: {
    keyField: 'orderId',
  },
  autoResize: true,
  height: '100%',
  border: true,
  stripe: true,
  columns: [
    { type: 'checkbox', width: 50 },
    { title: '序号', type: 'seq', width: 50 },
    {
      title: '平台信息',
      field: 'platformInfo',
      slots: { default: 'platformInfo' },
      showOverflow: false,
      width: 250,
    },
    {
      title: '经营平台信息',
      field: 'extraPlatformInfo',
      slots: { default: 'extraPlatformInfo' },
      showOverflow: false,
      width: 230,
    },
    {
      title: '买家信息',
      field: 'receiverInfo',
      slots: { default: 'receiverInfo' },
      showOverflow: false,
      width: 150,
    },
    {
      title: '采购商品信息',
      field: 'platformProductInfo',
      slots: { default: 'platformProductInfo' },
      showOverflow: false,
      width: 200,
    },
    {
      title: '采购时间',
      field: 'platformBuyTime',
      slots: { default: 'platformBuyTime' },
      showOverflow: false,
      width: 230,
    },
    {
      title: '采购金额',
      field: 'platformBuyPrice',
      slots: { default: 'platformBuyPrice' },
      showOverflow: false,
      width: 120,
    },
    {
      title: '运单信息',
      field: 'platformDeliveryNo',
      slots: { default: 'platformDeliveryNo' },
      showOverflow: false,
      width: 227,
    },
    {
      title: '售后编号',
      field: 'refundNo',
      showOverflow: false,
      width: 150,
    },
    {
      title: '妥投图',
      field: 'deliveredImg',
      slots: { default: 'deliveredImg' },
      showOverflow: false,
      width: 120,
    },
    {
      title: '中转仓信息',
      field: 'transferWarehouseInfo',
      slots: { default: 'transferWarehouseInfo' },
      showOverflow: false,
      width: 227,
    },
    {
      title: '留言信息',
      field: 'messageInformation',
      slots: { default: 'messageInformation' },
      showOverflow: false,
      width: 200,
    },
    {
      title: '预上网状态',
      field: 'preOnlineStatus',
      slots: { default: 'preOnlineStatus' },
      showOverflow: false,
      width: 150,
    },
    {
      slots: { default: 'action' },
      field: 'action',
      fixed: 'right',
      title: '操作',
      width: 100,
      showOverflow: false,
    },
  ],
  keepSource: true,
  pagerConfig: {},
  proxyConfig: {
    ajax: {
      query: async ({ page }, formValues) => {
        const data = requestParams(formValues);
        const { rows, total } = await orderItemListApi({
          pageNum: page.currentPage,
          pageSize: page.pageSize,
          ...data,
        });
        clearTableSelection();
        tableTotal.value = total;
        formParams.value = formValues;
        return {
          total,
          items: rows,
        };
      },
    },
  },
  toolbarConfig: {
    // 是否显示搜索表单控制按钮
    // @ts-ignore 正式环境时有完整的类型声明
    search: true,
  },
};
const [Grid, gridApi] = useVbenVxeGrid({
  formOptions,
  gridOptions,
});

getChannelListApi().then((res) => {
  gridApi.formApi.updateSchema([
    {
      component: 'Select',
      fieldName: 'purchaseChannelId',
      componentProps: {
        options: res.map((item: Record<string, any>) => ({
          label: item.name,
          value: item.id,
        })),
        clearable: true,
      },
      label: '采购渠道',
    },
  ]);
});

getBuyerList().then((res) => {
  gridApi.formApi.updateSchema([
    {
      componentProps: {
        options: res.map((item: Record<string, any>) => ({
          label: item.name,
          value: item.id,
        })),
      },
      fieldName: 'buyerAccountId',
    },
  ]);
});

// tabs
const orderStatus = ref<number | string | undefined>('all');

const handleClick = (tab: TabsPaneContext) => {
  orderStatus.value = tab.props.name;
  gridApi.reload();
};

function isShowShippingByOrderStatus(orderStatus) {
  return orderStatus == 1 || orderStatus == 2 || orderStatus == 3;
}

function isORDERPLACING(orderStatus: number | string) {
  return [0].includes(+orderStatus);
}

// table 操作栏

const [Drawer, drawerApi] = useVbenDrawer({
  // 连接抽离的组件
  connectedComponent: OrderInfoVue,
  onClosed() {
    const { refresh } = drawerApi.getData();
    refresh && gridApi.query();
  },
});

function openOrder(row: OrderInfo, isEdit = false) {
  drawerApi
    .setData({
      orderId: row.orderId,
      isEdit,
    })
    .open();
}

// 多选撤销
const selectRowEvent = () => {
  const $grid = gridApi.grid;
  if ($grid) {
    const selectRecords = $grid.getCheckboxRecords();
    const selectReserveRecords = $grid.getCheckboxReserveRecords();
    // 为了解决类型错误，明确指定数组类型
    const selectRows: any[] = [...selectReserveRecords, ...selectRecords];
    const orderIdList = selectRows.map((row) => row.orderId);
    if (orderIdList.length > 0) {
      // 为了避免类型错误，将 orderIdList 显式转换为合适的类型
      onCancelOrder(orderIdList as number[]);
    } else {
      ElMessage.error('请先选择订单');
    }
  }
};

// 撤销二次确认
const onCancelOrder = (orderIds: number[] = []) => {
  ElMessageBox.prompt(
    '<strong style="font-size: var(--el-font-size-extra-large)">确认是否将这些订单进行撤销操作？</strong><p style="padding-top:10px;">如撤销成功后，将不会推送至平台进行下单；</p><p style="padding-buttom:10px;">可在 “已失效”状态中查看；</p>',
    {
      title: '撤销订单',
      showInput: true,
      confirmButtonText: '确认撤销',
      cancelButtonText: '取消',
      showCancelButton: true,
      dangerouslyUseHTMLString: true,
      inputType: 'textarea',
      inputPlaceholder: '请输入备注',
      inputValidator: (value) => {
        if (`${value}`.length === 0) {
          return '请输入备注';
        }
        return true;
      },
      beforeClose: (action, instance, done) => {
        if (action === 'confirm') {
          instance.confirmButtonLoading = true;
          cancelOrderApi({ remark: instance.inputValue, orderIds })
            .then(() => {
              // 清空选择
              ElMessage.success('撤销操作成功！');
              done();
              gridApi.query();
            })
            .finally(() => {
              instance.confirmButtonLoading = false;
            });
        } else {
          done();
        }
      },
    },
  );
};

// 新建订单
const [AddDrawer, addDrawerApi] = useVbenDrawer({
  // 连接抽离的组件
  connectedComponent: AddOrder,
  onClosed() {
    const { refresh } = addDrawerApi.getData();
    refresh && gridApi.query();
  },
});
function openAdd() {
  addDrawerApi.open();
}

// 导出1000记录
// const exportData = async () => {
//   const values: FormValues = await gridApi.formApi.getValues();
//   const data = requestParams(values);
//   data.pageSize = 1000;
//   data.pageNum = 1;

//   exportOrderItemListApi(data);
// };

// 排序
function orderByTypeChange() {
  gridApi.reload();
}
// 设置订单标签
const [BatchOrderLabelModel, batchSetOrderLabelModelApi] = useVbenModal({
  // 连接抽离的组件
  connectedComponent: BatchSetOrderLabelModel,
  onOpenChange(isOpen) {
    if (!isOpen) {
      const { refresh } = batchSetOrderLabelModelApi.getData();
      refresh && gridApi.reload();
    }
  },
});

// 获取表格选中的行
function getCheckboxRecords() {
  const $grid = gridApi.grid;
  const selectRecords = $grid.getCheckboxRecords();
  const selectReserveRecords = $grid.getCheckboxReserveRecords();
  // 为了解决类型错误，明确指定数组类型
  const selectRows: any[] = [...selectReserveRecords, ...selectRecords];
  return selectRows;
}

// 批量设置标签
function openBatchSetOrderLabelModel() {
  const $grid = gridApi.grid;
  const selectRecords = $grid.getCheckboxRecords();
  const selectReserveRecords = $grid.getCheckboxReserveRecords();
  // 为了解决类型错误，明确指定数组类型
  const selectRows: any[] = [...selectReserveRecords, ...selectRecords];
  const orderIds = selectRows.map((row) => row.orderId);
  if (orderIds.length === 0) {
    return ElMessage({
      message: '请选择需要设置标签的订单',
      type: 'error',
    });
  }
  batchSetOrderLabelModelApi
    .setData({
      orderIds,
    })
    .open();
}
function openSetOrderLabelModel(row: OrderInfo) {
  batchSetOrderLabelModelApi
    .setData({
      orderIds: [row.orderId],
      labelIds: row.labelIds,
    })
    .open();
}

// 设置速蜂备注
function onChangeSysNote(row: OrderInfo) {
  ElMessageBox.prompt('请输入速蜂备注：', '修改备注', {
    showInput: true,
    confirmButtonText: '确认',
    cancelButtonText: '取消',
    showCancelButton: true,
    dangerouslyUseHTMLString: true,
    inputType: 'textarea',
    inputValue: row.sysNote,
    inputPlaceholder: '请输入备注',
    beforeClose: (action, instance, done) => {
      if (action === 'confirm') {
        instance.confirmButtonLoading = true;
        setOrderSysNoteApi({
          sysNote: instance.inputValue,
          orderId: row.orderId,
        })
          .then(() => {
            // 清空选择
            ElMessage.success('撤销操作成功！');
            done();
            gridApi.query();
          })
          .finally(() => {
            instance.confirmButtonLoading = false;
          });
      } else {
        done();
      }
    },
  });
}

// 匹配B单
// 新建订单
const [BuyBOrderDrawer, buyBOrderDrawerApi] = useVbenDrawer({
  // 连接抽离的组件
  connectedComponent: BuyBOrder,
  onClosed() {
    const { refresh } = buyBOrderDrawerApi.getData();
    refresh && gridApi.query();
  },
  destroyOnClose: true,
});

function matchBOrder(row: OrderInfo) {
  function goBuyBOrder(row: OrderInfo) {
    buyBOrderDrawerApi.setData(row).open();
  }
  if (row?.waybillSn) {
    ElMessageBox.confirm(
      '发现该订单已有B单号，避免重复购买，是否继续匹配？',
      '提示',
      {
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: '继续',
        cancelButtonText: '取消',
      },
    ).then(() => {
      // 去购买B单号
      goBuyBOrder(row);
    });
  } else {
    goBuyBOrder(row);
  }
}

// 物流跟踪
const [LogisticsTrackingDrawer, logisticsTrackingDrawerApi] = useVbenDrawer({
  connectedComponent: LogisticsTracking,
});
function openLogisticsTracking(row: Record<string, any>) {
  const { waybillSn, platformDeliveryNo } = row;
  if (!waybillSn && !platformDeliveryNo) {
    return ElMessageBox.alert('暂时没有可查询的物流单号！', {
      title: '提示',
      type: 'warning',
    });
  }

  logisticsTrackingDrawerApi.setData({ row }).open();
}

// 确认费用
const selectRow = ref<null | OrderInfo>(null);
const [CheckCostOrderModel, needCheckCostOrderModelApi] = useVbenModal({
  title: '中转仓价格确认',
  cancelText: '拒绝',
  onCancel() {
    stagingBuyRejectApi({
      fqOrderNo: selectRow.value?.orderNo,
      shippingFee: selectRow.value?.shippingCost,
    }).then(() => {
      ElMessage.success('拒绝成功');
    });
  },
  onConfirm() {
    stagingBuyConfirmApi({
      fqOrderNo: selectRow.value?.orderNo,
      shippingFee: selectRow.value?.shippingCost,
      channelCode: selectRow.value?.logistics_channel,
    }).then(() => {
      ElMessage.success('确认成功！');
    });
  },
});

function onNeedCheckCostOrder(row: OrderInfo) {
  selectRow.value = row;
  needCheckCostOrderModelApi.open();
}

// 查看物流
const [LogisticsInfoModel, logisticsInfoModelApi] = useVbenModal({
  title: '物流状态明细',
  class: 'min-w-500px',
  showConfirmButton: false,
  connectedComponent: LogisticsInfoModelTem,
});

function onLogisticsInfoModel(row: OrderInfo) {
  logisticsInfoModelApi.setData({ row }).open();
}

// 清空表格选中状态
function clearTableSelection() {
  const $grid = gridApi.grid;
  if ($grid) {
    $grid.clearCheckboxRow();
    $grid.clearCheckboxReserve();
  }
}

// 上传面单
const [UploadShippingModel, UploadShippingModelApi] = useVbenModal({
  title: '上传面单',
  class: 'w-700px min-h-500px',
  cancelText: '取消',
  connectedComponent: UploadShippingModelTem,
  onOpenChange(isOpen) {
    if (!isOpen) {
      const { refresh } = UploadShippingModelApi.getData();
      refresh && gridApi.query();
    }
  },
});

function onUploadShipping(row: OrderInfo) {
  selectRow.value = row;
  UploadShippingModelApi.setData({
    orderId: row.orderId,
    expressLabelUrl: row.expressLabelUrl,
  }).open();
}

// 查看面单expressLabelUrl
const [ExpressLabelModel, expressLabelModelApi] = useVbenModal({
  title: '查看面单',
  class: 'w-500px min-h-500px',
  showConfirmButton: false,
  cancelText: '关闭',
  connectedComponent: ExpressLabelModalTem,
});

function onExpressLabelModel(expressLabelUrl: string) {
  expressLabelModelApi.setData({ expressLabelUrl }).open();
}

function afterSales(row: OrderInfo) {
  const options: any = {
    type: 'info',
    showCancelButton: true,
    confirmButtonText: '确定',
    cancelButtonText: '取消',
  };
  ElMessageBox.confirm('该订单确认申请售后吗？', '提示', options).then(
    () => {
      applyRefundApi({
        orderId: row.orderId,
      }).then(() => {
        ElMessage.success('操作成功');
        gridApi.query();
      });
    },
    () => {},
  );
}

function preOnline(row: OrderInfo) {
  const options: any = {
    type: 'info',
    showCancelButton: true,
    confirmButtonText: '确定',
    cancelButtonText: '取消',
  };
  ElMessageBox.confirm(
    '确定是否要开启这个订单的预上网？',
    '开启预上网',
    options,
  ).then(
    () => {
      preOnlineApi({
        orderIdList: [row.orderId],
      }).then(
        () => {
          ElMessage.success('开启成功');
          clearTableSelection();
        },
        (error) => {
          if (error.code == 300) {
            const errMsg = error.msg.replaceAll('\n', '<br>');
            console.log(errMsg, 'errMsg');
            ElMessageBox.alert(errMsg, '提示', {
              confirmButtonText: '确定',
              dangerouslyUseHTMLString: true,
            }).then((res) => {});
          }
        },
      );
    },
    () => {},
  );
}

function batchPreOnline() {
  const selectRows = getCheckboxRecords();
  if (selectRows.length === 0) {
    ElMessage({
      message: '请选择订单',
      type: 'error',
    });
    return false;
  }
  const options: any = {
    type: 'info',
    showCancelButton: true,
    confirmButtonText: '确定',
    cancelButtonText: '取消',
  };
  ElMessageBox.confirm(
    `确定是否要开启这${selectRows.length}个订单的预上网？`,
    '开启预上网',
    options,
  ).then(
    () => {
      const params = {
        orderIdList: selectRows.map((item) => item.orderId),
      };
      preOnlineApi(params).then(
        () => {
          ElMessage.success('开启成功');
          const $grid = gridApi.grid;
          $grid.clearCheckboxRow();
          $grid.clearCheckboxReserve();
        },
        (error) => {
          if (error.code == 300) {
            const errMsg = error.msg.replaceAll('\n', '<br>');
            console.log(errMsg, 'errMsg');
            ElMessageBox.alert(errMsg, '提示', {
              confirmButtonText: '确定',
              dangerouslyUseHTMLString: true,
            }).then((res) => {});
          }
        },
      );
    },
    () => {},
  );
  console.log(selectRows, 'selectRows');
}

// 设置买家号
function checkCanSetbuyer() {
  const selectRows = getCheckboxRecords();
  if (selectRows.length === 0) {
    ElMessage({
      message: '请选择需要设置买家号的订单',
      type: 'error',
    });
    return false;
  }
  if (selectRows.some((item) => !item.orderMode || +item.orderMode === 0)) {
    ElMessage({
      message: '代采订单无法分配买家号！',
      type: 'error',
    });

    return false;
  }

  return true;
}

async function buyerOperate(command: string) {
  if (!checkCanSetbuyer()) return;
  const buyerAccountForm = ref<Record<string, any>>({});
  const apiFn = () => {
    const selectRows = getCheckboxRecords();
    buyerAccountOperateApi({
      buyerAccountId: buyerAccountForm.value.buyerAccountId,
      orderIdList: selectRows.map((item) => item.orderId),
    }).then(() => {
      ElMessage.success('操作成功');
      gridApi.query();
    });
  };
  switch (command) {
    case 'delete': {
      buyerAccountForm.value.buyerAccountId = 0;
      apiFn();
      break;
    }
    case 'set': {
      const buyerList = await getBuyerList();
      const buyerAccountFormRef = ref();
      ElMessageBox({
        customClass: 'h-[300px]',
        customStyle: {
          maxWidth: '450px',
        },
        showCancelButton: true,
        title: '提示',
        message: () => (
          <div class="h-[calc(300px-110px)] w-[calc(450px-30px)]">
            <ElForm
              model={buyerAccountForm}
              ref={(ref) => (buyerAccountFormRef.value = ref)}
              rules={{
                buyerAccountId: [
                  {
                    required: true,
                    message: '请选择买家号',
                    trigger: 'change',
                  },
                ],
              }}
            >
              <ElFormItem class="ml-4" label="选择买家号" prop="buyerAccountId">
                <ElSelect
                  clearable
                  modelValue={buyerAccountForm.value.buyerAccountId}
                  onChange={(val) =>
                    (buyerAccountForm.value.buyerAccountId = val)
                  }
                >
                  {buyerList.map((item: Record<string, any>) => (
                    <ElOption
                      key={item.id}
                      label={item.name}
                      value={item.id}
                    ></ElOption>
                  ))}
                </ElSelect>
              </ElFormItem>
            </ElForm>
          </div>
        ),
        async beforeClose(action, instance, done) {
          if (action === 'confirm') {
            const valid = await buyerAccountFormRef.value?.validate();
            if (!valid) return false;
            await apiFn();
            done();
          } else {
            done();
          }
        },
      });
      break;
    }
  }
}
</script>

<template>
  <div class="h-full">
    <ElTabs v-model="orderStatus" class="tab" @tab-click="handleClick">
      <ElTabPane label="全部" name="all" />
      <ElTabPane label="待拍单" name="0" />
      <ElTabPane label="拍单中" name="1" />
      <ElTabPane name="2" label="已拍单" />
      <ElTabPane label="已完成" name="3" />
      <ElTabPane label="已失效" name="4" />
    </ElTabs>
    <div
      class="px-4"
      style="height: calc(100% - 70px - 15px); overflow: hidden"
    >
      <Grid>
        <template #form-labelIds="slotProps">
          <SelectOrderLabel v-bind="slotProps" />
        </template>
        <template #platformInfo="{ row }">
          <FieldTemplateGrid field="platformInfo" :row="row" />
        </template>
        <template #extraPlatformInfo="{ row }">
          <FieldTemplateGrid field="extraPlatformInfo" :row="row" />
        </template>
        <template #receiverInfo="{ row }">
          <FieldTemplateGrid field="receiverInfo" :row="row" />
        </template>
        <template #platformProductInfo="{ row }">
          <FieldTemplateGrid field="platformProductInfo" :row="row" />
        </template>
        <template #platformBuyTime="{ row }">
          <FieldTemplateGrid field="platformBuyTime" :row="row" />
        </template>
        <template #platformBuyPrice="{ row }">
          <FieldTemplateGrid field="platformBuyPrice" :row="row" />
        </template>
        <template #platformDeliveryNo="{ row }">
          <FieldTemplateGrid
            field="platformDeliveryNo"
            :row="row"
            :handler="{
              platformDeliveryNo: {
                viewLogisticsTracking: (row: OrderInfo) => {
                  onLogisticsInfoModel(row);
                },
              },
            }"
            show-action
            @reload="gridApi.query()"
          />
        </template>
        <template #messageInformation="{ row }">
          <FieldTemplateGrid field="messageInformation" :row="row" />
        </template>
        <template #deliveredImg="{ row }">
          <FieldTemplateGrid field="deliveredImg" :row="row" />
        </template>
        <template #transferWarehouseInfo="{ row }">
          <FieldTemplateGrid
            field="transferWarehouseInfo"
            :handler="{
              transferWarehouseInfo: {
                onOpen: (expressLabelUrl: string) => {
                  onExpressLabelModel(expressLabelUrl);
                },
                viewLogisticsTracking: (row: OrderInfo) => {
                  onLogisticsInfoModel(row);
                },
              },
            }"
            :row="row"
          />
        </template>
        <template #preOnlineStatus="{ row }">
          <FieldTemplateGrid field="preOnlineStatus" :row="row" />
        </template>
        <template #toolbar-tools>
          <!-- <VbenButton @click="exportData">{{
            $t('page.order.exportDetails')
          }}</VbenButton> -->
          <ElSelect
            v-model="orderByType"
            placeholder="Select"
            size="large"
            class="mr-2"
            style="width: 180px"
            @change="orderByTypeChange"
          >
            <ElOption
              v-for="orderBy in orderByTypeList"
              :key="orderBy.value"
              :label="orderBy.label"
              :value="orderBy.value"
            >
              <div class="flex items-center">
                {{ orderBy.label }}
                <i v-if="orderBy.icon" :class="orderBy.icon"></i>
              </div>
            </ElOption>
          </ElSelect>
          <downloadPage
            :total="tableTotal"
            :ajax="exportOrderItemListApi"
            :get-params="
              async () => {
                return requestParams(await gridApi.formApi.getValues());
              }
            "
          >
            {{ $t('page.order.exportDetails') }}
          </downloadPage>
        </template>
        <template #toolbar-actions>
          <VbenButton @click="openAdd" key="add"> 开始下单 </VbenButton>
          <VbenButton
            class="ml-2"
            v-if="orderStatus && ![4].includes(+orderStatus)"
            @click="openBatchSetOrderLabelModel"
          >
            批量设置标签
          </VbenButton>
          <VbenButton
            class="ml-2"
            v-if="orderStatus && isORDERPLACING(orderStatus)"
            variant="destructive"
            @click="selectRowEvent"
          >
            撤销
          </VbenButton>
          <ElDropdown
            key="add1"
            placement="top"
            size="large"
            v-if="canSelfPurchase && orderStatus && [0].includes(+orderStatus)"
            @command="buyerOperate"
          >
            <VbenButton class="ml-2">
              买家号操作
              <i class="icon-[mdi--keyboard-arrow-down] text-base"></i>
            </VbenButton>
            <template #dropdown>
              <ElDropdownMenu>
                <ElDropdownItem command="set"> 分配买家号 </ElDropdownItem>
                <ElDropdownItem command="delete"> 删除买家号 </ElDropdownItem>
              </ElDropdownMenu>
            </template>
          </ElDropdown>
          <VbenButton
            class="ml-2"
            variant="destructive"
            @click="buyerOperate('delete')"
            v-if="canSelfPurchase && orderStatus && [1].includes(+orderStatus)"
          >
            删除买家号
          </VbenButton>
          <VbenButton class="ml-2" @click="batchPreOnline">
            批量预上网
          </VbenButton>
        </template>

        <template #action="{ row }">
          <div class="operations flex size-full flex-col justify-between">
            <ElLink
              @click="openOrder(row)"
              class="button"
              underline="never"
              type="primary"
              :key="`add${row.orderId}`"
            >
              查看
            </ElLink>

            <ElLink
              v-if="orderStatus && ![4].includes(+orderStatus)"
              @click="openSetOrderLabelModel(row)"
              class="button"
              underline="never"
              type="primary"
              :key="`setTag${row.orderId}`"
            >
              标签
            </ElLink>

            <ElLink
              v-if="isORDERPLACING(row.orderStatus)"
              @click="openOrder(row, true)"
              class="button"
              underline="never"
              type="primary"
              :key="`edit${row.orderId}`"
            >
              编辑
            </ElLink>

            <ElLink
              class="button"
              @click="onChangeSysNote(row)"
              underline="never"
              type="primary"
              :key="`remark${row.orderId}`"
            >
              速蜂备注
            </ElLink>

            <!--            <ElLink
              class="button"
              v-if="![4].includes(+row.orderStatus)"
              @click="matchBOrder(row)"
              underline="never"
              type="primary"
              :key="`buyBOrder${row.orderId}`"
            >
              匹配B单
            </ElLink>

            <ElLink
              class="button"
              v-if="![4].includes(+row.orderStatus)"
              @click="openLogisticsTracking(row)"
              underline="never"
              type="primary"
              :key="`logisticsTracking${row.orderId}`"
            >
              包裹跟踪
            </ElLink>-->

            <ElLink
              class="button"
              v-if="row.needCheck"
              @click="onNeedCheckCostOrder(row)"
              underline="never"
              type="primary"
              :key="`needCheck${row.orderId}`"
            >
              确认费用
            </ElLink>

            <ElLink
              class="button"
              v-if="isORDERPLACING(row.orderStatus)"
              @click="onCancelOrder([row.orderId])"
              underline="never"
              type="danger"
              :key="`cancel${row.orderId}`"
            >
              撤单
            </ElLink>

            <ElLink
              class="button"
              v-if="
                [2].includes(+row.shippingLabelType) &&
                [1].includes(+row.orderType) &&
                isShowShippingByOrderStatus(row.orderStatus)
              "
              @click="onUploadShipping(row)"
              underline="never"
              type="primary"
              :key="`shipping${row.orderId}`"
            >
              上传面单
            </ElLink>
            <ElLink
              class="button"
              v-if="
                row.orderStatus == 1 ||
                row.orderStatus == 2 ||
                row.orderStatus == 3
              "
              @click="afterSales(row)"
              underline="never"
              type="primary"
              :key="`afterSales${row.orderId}`"
            >
              申请售后
            </ElLink>
            <ElLink
              v-if="row.orderType == 1"
              class="button"
              @click="preOnline(row)"
              underline="never"
              type="primary"
              :key="`preOnline${row.orderId}`"
            >
              预上网
            </ElLink>
          </div>
        </template>
      </Grid>
    </div>
    <Drawer />
    <AddDrawer />
    <BatchOrderLabelModel />
    <BuyBOrderDrawer />
    <LogisticsTrackingDrawer />
    <CheckCostOrderModel>
      <div class="flex flex-col items-center justify-center">
        <div>为了保证中转仓快速送出包裹，等待您的价格确认。</div>
        <ElDescriptions
          direction="vertical"
          border
          size="large"
          label-width="200"
          class="my-8"
        >
          <ElDescriptionsItem label="包裹明细">
            <ul>
              <li>
                体积（cm³）：{{ selectRow && selectRow?.dimensionsVo.volume }}
              </li>
              <li>重量(KG)：{{ selectRow && selectRow?.weight }}</li>
              <li>
                长宽高：{{ selectRow && selectRow?.dimensionsVo.length }}*{{
                  selectRow && selectRow?.dimensionsVo.width
                }}*{{ selectRow && selectRow?.dimensionsVo.height }}
                cm
              </li>
            </ul>
          </ElDescriptionsItem>
          <ElDescriptionsItem label="价格明细">
            <ul>
              <li>运费价格($）：{{ selectRow?.shippingCost }}</li>
            </ul>
          </ElDescriptionsItem>
        </ElDescriptions>
      </div>
    </CheckCostOrderModel>

    <LogisticsInfoModel />

    <ExpressLabelModel />

    <UploadShippingModel />
  </div>
</template>

<style lang="scss" scoped>
:deep(.el-tabs__nav-wrap) {
  padding: 8px;
  background-color: #fff;
}

:deep(.operations) {
  .button {
    margin: 0;
  }

  .button + .button {
    margin-left: 0;
  }
}
</style>
